import { useTranslation } from 'react-i18next';
import { Check, Search, X } from 'lucide-react';

import { numberFormatter } from '../../utils/calorieLog';
import { foodKey, searchedFoodName } from '../../utils/foodSearch';
import '../../styles/pages/calorias.css';

/*
 * The food search's pieces, shared by the calorie tracker's add-food sheet and the diet plan's food editor: the search
 * field, the results, the amount with its slider, and the calories and macros of that amount. Their words are the
 * tracker's (pages.CalorieTracker.ui), where the sheet started.
 */

const useUi = () => {
  const { t } = useTranslation();
  return (key, options) => t(`pages.CalorieTracker.ui.${key}`, options);
};

// Search results are per 100 g; scale them to the amount eaten
const scale = (value, grams) => Math.round(((value || 0) * grams) / 100);

// TACO names: "Frango, peito, sem pele, grelhado" → the food first, its detail after
const splitName = (name = '') => {
  const at = name.indexOf(',');
  return at === -1 ? [name, ''] : [name.slice(0, at), name.slice(at + 1).trim()];
};

export const FoodName = ({ name }) => {
  const [head, rest] = splitName(name);
  return (
    <>
      <b>{head}</b>
      {rest && <span>, {rest}</span>}
    </>
  );
};

/** The search field, with its own clear button (the browser's draws in system blue). */
export const FoodSearchField = ({ inputRef, value, onChange }) => {
  const ui = useUi();
  const clear = () => {
    onChange('');
    inputRef?.current?.focus();
  };
  return (
    <div className="mt-cal-search">
      <Search aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={ui('tabSearch')}
        aria-label={ui('tabSearch')}
        autoComplete="off"
      />
      {value && (
        <button type="button" className="mt-cal-search__clear" onClick={clear} aria-label={ui('clearSearch')}>
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

/**
 * What a search found: "Buscando…", nothing, or the foods with their kcal per 100 g. A food already picked shows its
 * grams instead (`pickedGrams` gives them, or nothing).
 */
export const FoodResults = ({ results, isSearching, hasSearched, onPick, pickedGrams = () => null }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ui = useUi();
  const formatNumber = numberFormatter(language);
  const formatGrams = (value) => value.toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US', { maximumFractionDigits: 1 });

  if (isSearching) return <p className="mt-cal-sheet__msg">{ui('searching')}</p>;
  if (hasSearched && results.length === 0) return <p className="mt-cal-sheet__msg">{t('pages.CalorieTracker.noResults')}</p>;
  return (
    <ul className="mt-cal-picks">
      {results.map((food) => {
        const grams = pickedGrams(food);
        return (
          <li key={foodKey(food)}>
            <button type="button" className={`mt-cal-pick${grams ? ' is-picked' : ''}`} onClick={() => onPick(food)}>
              <span className="mt-cal-pick__text">
                <span className="mt-cal-pick__name">
                  <FoodName name={searchedFoodName(food, language)} />
                </span>
                {food.category && <span className="mt-cal-pick__meta">{food.category.split(',')[0]}</span>}
              </span>
              {grams ? (
                <span className="mt-cal-pick__kcal">
                  <span className="mt-cal-pick__tag">
                    <Check aria-hidden="true" />
                    <span className="mt-sr-only">{ui('selectionTitle')}: </span>
                    {`${formatGrams(grams)} g`}
                  </span>
                  <small>{ui('kcal', { kcal: formatNumber(scale(food.calories, grams)) })}</small>
                </span>
              ) : (
                <span className="mt-cal-pick__kcal">
                  {ui('kcal', { kcal: formatNumber(food.calories) })}
                  <small>{ui('per100')}</small>
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

/**
 * The amount, big and typed, with a slider under it for what a meal usually holds (up to `max`; the field takes up to
 * `limit`). `value` is the field's text.
 */
export const AmountPicker = ({ value, onChange, inputRef, label, unit = 'g', max = 500, limit = 5000 }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const ui = useUi();
  const amount = Number(value);
  const sliderValue = Math.min(Math.max(amount || 0, 0), max);
  const marks = [0, 1, 2, 3, 4, 5].map((step) => Math.round((max * step) / 5));
  const formatAmount = (number) => number.toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US', { maximumFractionDigits: 1 });
  return (
    <>
      <label className="mt-cal-grams">
        <input
          ref={inputRef}
          type="number"
          inputMode="decimal"
          min="1"
          max={limit}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={label || ui('amount')}
        />
        <span aria-hidden="true">{unit}</span>
      </label>
      <input
        className="mt-cal-range"
        type="range"
        min="0"
        max={max}
        step="5"
        value={sliderValue}
        onChange={(event) => onChange(event.target.value)}
        style={{ '--mt-range-fill': `${(sliderValue / max) * 100}%` }}
        aria-label={ui('amountSlider')}
        aria-valuetext={`${formatAmount(sliderValue)} ${unit}`}
      />
      <div className="mt-cal-range__marks" aria-hidden="true">
        {marks.map((mark) => (
          <span key={mark}>{mark === max ? `${mark} ${unit}` : mark}</span>
        ))}
      </div>
    </>
  );
};

/** The calories and macros of the amount weighed (`values`), or dashes while the amount isn't valid. */
export const FoodFacts = ({ values }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const formatNumber = numberFormatter(language);
  return (
    <p className="mt-cal-facts" aria-live="polite">
      <span className="mt-cal-facts__kcal">
        <b>{values ? formatNumber(values.calories) : '–'}</b> kcal
      </span>
      {['protein', 'carbs', 'fat'].map((macro) => (
        <span key={macro}>
          {t(`pages.DietPlan.${macro}`)} <b>{values ? `${Math.round(values[macro] || 0)} g` : '–'}</b>
        </span>
      ))}
    </p>
  );
};
